import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    TextField,
    Autocomplete,
    Stack,
    Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAlert } from "../../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import { useWarehouse } from "../../contexts/WarehouseContext";
import { documentsApi, DocumentApiResponse } from "../../services/documentsApi";
import { getTextFieldStyles, getAutocompleteStyles, getAutocompleteListboxStyles } from "../../styles/formStyles";
import { LabelSelector } from "../../components/Labels";
import { WarehouseSelector } from "../../components/WarehouseSelector";
import { ClientSelector } from "../../components/ClientSelector";
import { ProductSelector, ProductSelection } from "../../components/ProductSelector";
import CustomBox from "../../components/CustomBox/CustomBox";
import { addUserNotification } from "../../utils/userNotifications";

const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "#474747ff" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
        "&.Mui-focused fieldset": { borderColor: "primary.main" },
        "&.Mui-disabled fieldset": { borderColor: "#474747ff" },
    },
    "& .MuiInputLabel-root": { color: "text.secondary" },
    "& .MuiInputLabel-root.Mui-focused": { color: "text.secondary" },
    "& .MuiInputBase-input": { color: "text.primary" },
    "& .MuiInputBase-input.Mui-disabled": {
        color: "#ffffff",
        WebkitTextFillColor: "#ffffff",
        opacity: 1,
    },
};

const dateFieldStyles = {
    ...textFieldStyles,
    "& .MuiOutlinedInput-root": {
        ...textFieldStyles["& .MuiOutlinedInput-root"],
        cursor: "pointer",
    },
    "& input": { cursor: "pointer" },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
        filter: "invert(1)",
        cursor: "pointer",
        opacity: 0.9,
    },
};

const CreateDocument = () => {
    const { id } = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { t } = useTranslation();
    const { selectedWarehouse } = useWarehouse();

    const documentDateInputRef = useRef<HTMLInputElement>(null);
    const deliveryDateInputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [documentData, setDocumentData] = useState<DocumentApiResponse | null>(null);
    const [generatedDocumentNumber, setGeneratedDocumentNumber] = useState<string>('');
    const [isGeneratingNumber, setIsGeneratingNumber] = useState<boolean>(false);

    const preselectedType = searchParams.get('type') || '';

    // Form state
    const [formData, setFormData] = useState({
        document_type: preselectedType,
        document_date: new Date().toISOString().split("T")[0],
        delivery_date: '' as string,
        document_number: '',
        location_id: null as number | null,
        client_id: null as number | null,
        document_notes: '',
        document_currency: 'PLN',
        label_ids: [] as number[],
        products: [] as ProductSelection[],
    });

    const documentTypeOptions = useMemo(() => [
        { value: 'A', label: t('documents.types.A') || 'Awizacja' },
        { value: 'WZ', label: t('documents.types.WZ') || 'Wydanie z Magazynu' },
        { value: 'KOR', label: t('documents.types.KOR') || 'Korekta stanu magazynowego' },
        { value: 'PP', label: t('documents.types.PP') || 'Przesunięcie produktów' },
    ], [t]);

    const getDocumentTypeLabel = useCallback((documentType: string) => {
        const fromOptions = documentTypeOptions.find((option) => option.value === documentType)?.label;
        if (fromOptions) {
            return fromOptions;
        }

        return t(`documents.types.${documentType}`) || documentType;
    }, [documentTypeOptions, t]);

    // Load document data in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            const loadDocument = async () => {
                try {
                    setLoading(true);
                    const document = await documentsApi.getDocument(parseInt(id));
                    setDocumentData(document);
                    setFormData({
                        document_type: document.document_type || '',
                        document_date: document.document_date ? document.document_date.split("T")[0] : new Date().toISOString().split("T")[0],
                        delivery_date: document.delivery_date || '',
                        document_number: document.document_number || '',
                        location_id: document.location || selectedWarehouse?.id || null,
                        client_id: document.client || null,
                        document_notes: document.document_notes || '',
                        document_currency: document.document_currency || 'PLN',
                        label_ids: document.labels?.map((l: any) => l.id) || [],
                        products: document.products?.map((p: any) => ({
                            product_id: p.product_id,
                            quantity: p.quantity,
                            unit_price: p.unit_price || '',
                            product_name: p.product_name,
                            product_code: p.product_code ?? undefined,
                            storage_location_id: p.storage_location_id ?? undefined,
                            storage_location_code: p.storage_location_code ?? undefined,
                            source_storage_location_id: p.source_storage_location_id ?? undefined,
                            source_storage_location_code: p.source_storage_location_code ?? undefined,
                        })) || [],
                    });
                } catch (error: any) {
                    const errorMessage =
                        error.response?.data?.detail ||
                        error.message ||
                        "Błąd podczas pobierania dokumentu";
                    showAlert("error", errorMessage, 5000);
                    console.error("Błąd podczas pobierania dokumentu:", error);
                } finally {
                    setLoading(false);
                }
            };
            loadDocument();
        }
    }, [isEditMode, id, showAlert, selectedWarehouse?.id]);

    // Update location_id when selectedWarehouse changes (only for new documents)
    useEffect(() => {
        if (!isEditMode && selectedWarehouse?.id) {
            setFormData((prev) => ({
                ...prev,
                location_id: selectedWarehouse.id
            }));
        }
    }, [selectedWarehouse?.id, isEditMode]);

    // Generate document number for new documents
    const generateDocumentNumber = useCallback(async (documentType: string | null, locationId: number | null) => {
        if (!documentType || !locationId || isEditMode) {
            setGeneratedDocumentNumber('');
            return;
        }

        setIsGeneratingNumber(true);
        try {
            const number = await documentsApi.generateDocumentNumber(documentType, locationId);
            setGeneratedDocumentNumber(number);
            setFormData((prev) => ({ ...prev, document_number: number }));
        } catch (error: any) {
            console.error('Błąd podczas generowania numeru dokumentu:', error);
            setGeneratedDocumentNumber('');
        } finally {
            setIsGeneratingNumber(false);
        }
    }, [isEditMode]);

    // Generate document number when document_type or location_id changes (for new documents)
    useEffect(() => {
        if (!isEditMode && formData.document_type && formData.location_id) {
            generateDocumentNumber(formData.document_type, formData.location_id);
        }
    }, [formData.document_type, formData.location_id, isEditMode, generateDocumentNumber]);

    // Handle products change
    const handleProductsChange = useCallback((products: ProductSelection[]) => {
        setFormData((prev) => ({ ...prev, products }));
    }, []);

    const isKOR = formData.document_type === 'KOR';
    const isWZ = formData.document_type === 'WZ';
    const isPP = formData.document_type === 'PP';
    const requiresStorageLocation = isKOR || isWZ || isPP;

    // Format products for API
    const formatProductsForAPI = (products: ProductSelection[]) => {
        return products
            .filter((p) => isKOR ? p.quantity !== 0 : p.quantity > 0)
            .map((p) => {
                if (p.product_id) {
                    // Existing product
                    return {
                        product_id: p.product_id,
                        quantity: p.quantity,
                        unit_price: p.unit_price || '0.00',
                        ...(requiresStorageLocation && p.storage_location_id != null && { storage_location_id: p.storage_location_id }),
                        ...(isPP && p.source_storage_location_id != null && { source_storage_location_id: p.source_storage_location_id }),
                    };
                } else if (p.product) {
                    // New product
                    return {
                        product: {
                            name: p.product.name,
                            description: p.product.description,
                            category: p.product.category,
                            price: p.product.price,
                            ...(p.product.supplier_ids && p.product.supplier_ids.length > 0 && { supplier_ids: p.product.supplier_ids }),
                            ...(p.product.label_ids && p.product.label_ids.length > 0 && { label_ids: p.product.label_ids }),
                            ...(p.product.shelf_life_days != null && { shelf_life_days: p.product.shelf_life_days }),
                            ...(p.product.expiry_warning_days != null && { expiry_warning_days: p.product.expiry_warning_days }),
                            ...(p.product.block_expired_sales != null && { block_expired_sales: p.product.block_expired_sales }),
                        },
                        quantity: p.quantity,
                        unit_price: p.unit_price || p.product.price || '0.00',
                        ...(requiresStorageLocation && p.storage_location_id != null && { storage_location_id: p.storage_location_id }),
                        ...(isPP && p.source_storage_location_id != null && { source_storage_location_id: p.source_storage_location_id }),
                    };
                }
                return null;
            })
            .filter((p): p is NonNullable<typeof p> => p !== null);
    };

    // Validation
    const validateForm = (): string | null => {
        if (!formData.document_type) {
            return t("documents.validation.documentTypeRequired") || "Typ dokumentu jest wymagany";
        }
        if (requiresStorageLocation) {
            const lines = formData.products.filter((p) => isKOR ? p.quantity !== 0 : p.quantity > 0);
            for (let i = 0; i < lines.length; i++) {
                const p = lines[i];
                if (p.storage_location_id == null || p.storage_location_id === undefined) {
                    if (isKOR) {
                        return t("documents.validation.korStorageLocationRequired", { index: i + 1 })
                            || `Korekta: wybierz lokalizację magazynową (półkę) dla pozycji ${i + 1}.`;
                    }
                    if (isPP) {
                        return t("documents.validation.ppStorageLocationRequired", { index: i + 1 })
                            || `PP: wybierz docelową lokalizację magazynową dla pozycji ${i + 1}.`;
                    }
                    return t("documents.validation.wzStorageLocationRequired", { index: i + 1 })
                        || `WZ: wybierz lokalizację magazynową (półkę) dla pozycji ${i + 1}.`;
                }
                if (isPP && p.source_storage_location_id === p.storage_location_id) {
                    return `PP: lokalizacja skąd i dokąd muszą być różne dla pozycji ${i + 1}.`;
                }
            }
        }
        return null;
    };

    // Handle save
    const handleSave = async () => {
        const validationError = validateForm();
        if (validationError) {
            showAlert("error", validationError, 5000);
            return;
        }

        try {
            setSaving(true);

            const formattedProducts = formatProductsForAPI(formData.products);
            const locationId = formData.location_id || selectedWarehouse?.id || null;
            const clientId = formData.client_id !== null && formData.client_id !== undefined
                ? (typeof formData.client_id === 'number' ? formData.client_id : parseInt(String(formData.client_id)))
                : undefined;

            if (isEditMode && documentData) {
                // Update existing document
                await documentsApi.updateDocument(documentData.id, {
                    document_date: formData.document_date || undefined,
                    location_id: locationId || undefined,
                    client_id: clientId,
                    document_notes: formData.document_notes || undefined,
                    label_ids: formData.label_ids ?? [],
                    products: formattedProducts.length > 0 ? formattedProducts : undefined,
                });
                showAlert("success", t("documents.messages.updated") || "Dokument zaktualizowany");
            } else {
                // Create new document
                const createdDocument = await documentsApi.createDocument({
                    document_type: formData.document_type,
                    document_date: formData.document_date || undefined,
                    delivery_date: formData.delivery_date || undefined,
                    location_id: locationId || undefined,
                    client_id: clientId,
                    document_notes: formData.document_notes || undefined,
                    document_currency: formData.document_currency || "PLN",
                    label_ids: formData.label_ids ?? [],
                    products: formattedProducts.length > 0 ? formattedProducts : undefined,
                });

                const documentTypeCode = createdDocument.document_type || formData.document_type;
                const documentTypeLabel = getDocumentTypeLabel(documentTypeCode);
                addUserNotification({
                    text: t("notifications.documentCreated", {
                        documentType: documentTypeLabel,
                        documentNumber: createdDocument.document_number,
                        defaultValue: `${documentTypeLabel} (${createdDocument.document_number}) has been created`,
                    }),
                    translationKey: "notifications.documentCreated",
                    translationParams: {
                        documentTypeCode,
                        documentNumber: createdDocument.document_number,
                    },
                });

                showAlert("success", t("documents.messages.created") || "Dokument utworzony");
            }
            navigate("/documents");
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.detail ||
                error.message ||
                "Błąd podczas zapisywania dokumentu";
            showAlert("error", errorMessage, 5000);
            console.error("Błąd podczas zapisywania dokumentu:", error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    const showPrice = false;

    return (
        <Box>
            <CustomBox>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/documents")}
                        sx={{ color: "text.primary" }}
                    >
                    </Button>
                    <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {isEditMode ? t("documents.editDocument") : t("documents.addNewDocument")}
                    </Typography>
                </Stack>

                <Divider sx={{ mb: 3, borderColor: "#474747ff" }} />

                <Stack spacing={3}>
                    {/* Document Type */}
                    <Autocomplete
                        options={documentTypeOptions}
                        value={documentTypeOptions.find(opt => opt.value === formData.document_type) || null}
                        onChange={(_event, newValue) => {
                            setFormData((prev) => ({ ...prev, document_type: newValue ? newValue.value : '' }));
                        }}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        disabled={saving}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("documents.form.documentType")}
                                error={!formData.document_type}
                                helperText={!formData.document_type ? t("documents.validation.documentTypeRequired") : undefined}
                                required
                                fullWidth
                                margin="normal"
                                sx={getTextFieldStyles({ error: !formData.document_type })}
                            />
                        )}
                        fullWidth
                        sx={getAutocompleteStyles()}
                        ListboxProps={{
                            sx: getAutocompleteListboxStyles(),
                        }}
                    />

                    {/* Document Date */}
                    <TextField
                        label={t("documents.form.documentDate")}
                        type="date"
                        value={formData.document_date}
                        onChange={(e) => setFormData((prev) => ({ ...prev, document_date: e.target.value }))}
                        disabled={saving}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        inputRef={documentDateInputRef}
                        InputProps={{
                            onClick: () => documentDateInputRef.current?.showPicker?.(),
                        }}
                        sx={dateFieldStyles}
                    />

                    {/* Delivery Date (only for Awizacja) */}
                    {formData.document_type === 'A' && (
                        <TextField
                            label={t("documents.form.deliveryDate") || "Data dostawy"}
                            type="date"
                            value={formData.delivery_date}
                            onChange={(e) => setFormData((prev) => ({ ...prev, delivery_date: e.target.value }))}
                            disabled={saving}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            inputRef={deliveryDateInputRef}
                            InputProps={{
                                onClick: () => deliveryDateInputRef.current?.showPicker?.(),
                            }}
                            sx={dateFieldStyles}
                        />
                    )}

                    {/* Document Number */}
                    <TextField
                        label={t("documents.form.documentNumber")}
                        value={isEditMode ? formData.document_number : (generatedDocumentNumber || '')}
                        onChange={(e) => {
                            if (isEditMode) {
                                setFormData((prev) => ({ ...prev, document_number: e.target.value }));
                            }
                        }}
                        disabled={saving || !isEditMode || isGeneratingNumber}
                        helperText={
                            !isEditMode
                                ? (isGeneratingNumber
                                    ? (t("documents.form.generatingNumber") || "Generowanie numeru...")
                                    : (t("documents.form.documentNumberAutoGenerated") || "Numer zostanie wygenerowany automatycznie"))
                                : undefined
                        }
                        fullWidth
                        margin="normal"
                        sx={{
                            ...textFieldStyles,
                            "& .MuiFormHelperText-root": {
                                color: "#ffffff !important",
                            },
                        }}
                    />

                    {/* Warehouse */}
                    <WarehouseSelector
                        value={formData.location_id || selectedWarehouse?.id || null}
                        onChange={(value) => setFormData((prev) => ({ ...prev, location_id: value }))}
                        label={t("documents.form.location") || "Magazyn"}
                    />

                    {/* Client */}
                    <ClientSelector
                        value={formData.client_id || null}
                        onChange={(value) => setFormData((prev) => ({ ...prev, client_id: value }))}
                        label={t("documents.form.client")}
                    />

                    {/* Notes */}
                    <TextField
                        label={t("documents.form.notes")}
                        multiline
                        rows={3}
                        value={formData.document_notes}
                        onChange={(e) => setFormData((prev) => ({ ...prev, document_notes: e.target.value }))}
                        disabled={saving}
                        fullWidth
                        margin="normal"
                        sx={textFieldStyles}
                    />

                    {/* Currency */}
                    <TextField
                        label={t("documents.form.currency")}
                        value={formData.document_currency}
                        onChange={(e) => setFormData((prev) => ({ ...prev, document_currency: e.target.value }))}
                        disabled={saving}
                        fullWidth
                        margin="normal"
                        sx={textFieldStyles}
                    />

                    {/* Labels */}
                    <Box>
                        <LabelSelector
                            value={formData.label_ids || []}
                            onChange={(labelIds: number[]) => {
                                setFormData((prev) => ({ ...prev, label_ids: labelIds }));
                            }}
                            label={t("documents.form.labels")}
                        />
                    </Box>

                    {/* Products */}
                    {formData.document_type && (
                        <Box>
                            <ProductSelector
                                value={formData.products || []}
                                onChange={handleProductsChange}
                                showPrice={showPrice}
                                label={t("documents.form.products")}
                                showAddNewButton={formData.document_type === 'A'}
                                useTableSelector={true}
                                allowNegativeQuantity={isKOR}
                                requireStorageLocation={requiresStorageLocation}
                                excludeTempLocations={isWZ}
                                allowDuplicateProducts={isPP}
                                requireSourceStorageLocation={isPP}
                                showBundleSelector={isKOR || isPP}
                            />
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: '1px solid #474747ff' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/documents")}
                            disabled={saving}
                            sx={{
                                borderColor: "#474747ff",
                                color: "text.primary",
                                "&:hover": {
                                    borderColor: "rgba(255, 255, 255, 0.5)",
                                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                                },
                            }}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || !formData.document_type}
                            sx={{
                                minWidth: 100,
                                "&:disabled": {
                                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                                    color: "rgba(255, 255, 255, 0.3)",
                                },
                            }}
                        >
                            {saving ? <CircularProgress size={24} /> : (isEditMode ? t("common.saveChanges") : t("common.add"))}
                        </Button>
                    </Stack>
                </Stack>
            </CustomBox>
        </Box>
    );
};

export default CreateDocument;
